import {ActionStatus} from '@domain/pom'
import {Attachment} from '@hconnect/common/types'
import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {actionValidationConfig} from '../../common/validator/actionValidator'
import {validateItem} from '../../common/validator/validator'
import {FEATURE_NAMESPACE} from '../../consts'
import {useAddActionAttachments, useCreateAction, usePlantUpmId, useTimeZone} from '../../hooks'
import {QuestionItem, CreateActionItem, ActionItem} from '../../types'
import {dueDate} from '../../utils'
import {Attachments} from '../common'

import {ActionDialog} from './ActionDialog'

type Props = {
  open: boolean
  handleOpen: (open: boolean) => void
  onActionCreate?: (item: ActionItem) => void
  relatedQuestion?: QuestionItem
  showAttachments?: boolean
  showApprovers?: boolean
}

const resetFormValues = (
  dueDate: Date,
  relatedQuestion?: QuestionItem
): Partial<CreateActionItem> => {
  const initialValues: Partial<CreateActionItem> = {
    dueDate,
    title: '',
    description: '',
    status: ActionStatus.Pending,
    costLeadership: false
  }
  return relatedQuestion
    ? {
        ...initialValues,
        questionId: relatedQuestion.id,
        source: 'pom'
      }
    : initialValues
}

const validateCreateActionItem = (item: Partial<CreateActionItem>) =>
  validateItem(actionValidationConfig(), item)
const revokeAllBlobUrls = (files: StagedFile[]) => {
  files.forEach(({blobUrl}) => URL.revokeObjectURL(blobUrl))
}

type StagedFile = {file: File; blobUrl: string}

export const CreateActionDialog: React.FC<Props> = ({
  open,
  handleOpen,
  onActionCreate,
  relatedQuestion,
  showAttachments = false,
  showApprovers
}) => {
  const timeZone = useTimeZone()
  const plantId = usePlantUpmId()
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([])
  const stagedFilesRef = useRef(stagedFiles)
  stagedFilesRef.current = stagedFiles
  const {mutate: addAttachments} = useAddActionAttachments()

  useEffect(() => {
    return () => {
      revokeAllBlobUrls(stagedFilesRef.current)
    }
  }, [])

  const {mutate: createAction, isPending: creating} = useCreateAction((item) => {
    if (showAttachments && stagedFiles.length) {
      addAttachments({
        plantId,
        actionId: Number(item.id),
        files: stagedFiles.map(({file}) => file)
      })
    }
    revokeAllBlobUrls(stagedFiles)
    setStagedFiles([])
    handleOpen(false)
    onActionCreate?.(item)
  })

  const actionItem = resetFormValues(dueDate(Date.now(), timeZone), relatedQuestion)

  const handleSubmit = (item: Partial<CreateActionItem>) => {
    createAction(item as CreateActionItem)
  }

  const handleFilesAdded = useCallback((files: File[]) => {
    setStagedFiles((prev) => [
      ...prev,
      ...files.map((file) => ({file, blobUrl: URL.createObjectURL(file)}))
    ])
  }, [])

  const attachedFiles: Attachment[] = stagedFiles.map(({file, blobUrl}, index) => ({
    id: index,
    fileName: file.name,
    url: blobUrl,
    mediaType: file.type
  }))

  return (
    <ActionDialog
      open={open}
      handleOpen={(isOpen) => {
        if (!isOpen) {
          revokeAllBlobUrls(stagedFiles)
          setStagedFiles([])
        }
        handleOpen(isOpen)
      }}
      actionItem={actionItem}
      onSave={handleSubmit}
      onValidate={validateCreateActionItem}
      saving={creating}
      title={t('addAction.heading')}
      showApprovers={showApprovers}
    >
      {showAttachments && (
        <Attachments
          attachedFiles={attachedFiles}
          onUpload={handleFilesAdded}
          onDelete={(attachment) =>
            setStagedFiles((prev) => {
              const toRemove = prev[attachment.id]
              if (toRemove) URL.revokeObjectURL(toRemove.blobUrl)
              return prev.filter((_, i) => i !== attachment.id)
            })
          }
          inputId="upload-create-action-attachment-input"
        />
      )}
    </ActionDialog>
  )
}
